import { Component } from '@theme/component';

/**
 * Share Button Component
 * Provides social media sharing functionality with Web Share API fallback
 * 
 * @fires share - When share is triggered
 */
class ShareButton extends Component {
  /**
   * Called when component is connected to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    // Check if Web Share API is supported
    this.supportsWebShare = navigator.share !== undefined;

    // Check if we're on desktop using matchMedia
    this.isDesktop = window.matchMedia('(min-width: 750px)').matches;

    // Listen for media query changes
    this.mediaQuery = window.matchMedia('(min-width: 750px)');
    this.mediaQuery.addEventListener('change', (e) => {
      this.isDesktop = e.matches;
    });
  }

  /**
   * Handle share button click
   * @param {Event} event - Click event
   */
  async handleShare(event) {
    event.preventDefault();

    const productTitle = this.dataset.productTitle || '';
    const productUrl = this.dataset.productUrl || window.location.href;

    // Use Web Share API if supported and enabled
    if (this.supportsWebShare && this.dataset.useWebShare === 'true') {
      try {
        await navigator.share({
          title: productTitle,
          url: productUrl
        });

        // Track share event with Shopify Analytics if available
        this.trackShare('web-share-api');
      } catch (error) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Show platform-specific share menu
      this.toggleShareMenu();
    }
  }

  /**
   * Toggle visibility of share menu
   */
  toggleShareMenu() {
    const menu = this.refs.shareMenu;
    if (!menu) return;

    const isHidden = menu.getAttribute('aria-hidden') === 'true';
    menu.setAttribute('aria-hidden', isHidden ? 'false' : 'true');

    if (!isHidden) {
      // Close menu when clicking outside
      const closeMenu = (e) => {
        if (!this.contains(e.target)) {
          menu.setAttribute('aria-hidden', 'true');
          document.removeEventListener('click', closeMenu);
        }
      };

      // Delay to prevent immediate close
      setTimeout(() => {
        document.addEventListener('click', closeMenu);
      }, 0);
    }
  }

  /**
   * Handle platform-specific share
   * @param {Object} data - Data object with platform
   * @param {Event} event - Click event
   */
  handlePlatformShare({ platform }, event) {
    event.preventDefault();
    event.stopPropagation();

    const productTitle = this.dataset.productTitle || '';
    const productUrl = this.dataset.productUrl || window.location.href;
    const encodedUrl = encodeURIComponent(productUrl);
    const encodedTitle = encodeURIComponent(productTitle);

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'pinterest':
        const imageUrl = this.dataset.productImage || '';
        const encodedImage = encodeURIComponent(imageUrl);
        shareUrl = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedTitle}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
        break;
      case 'copy':
        this.copyToClipboard(productUrl);
        return;
      default:
        return;
    }

    // Open share window
    if (shareUrl && platform !== 'email') {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    } else if (platform === 'email') {
      window.location.href = shareUrl;
    }

    // Track share event
    this.trackShare(platform);

    // Close menu
    const menu = this.refs.shareMenu;
    if (menu) {
      menu.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Copy URL to clipboard
   * @param {string} url - URL to copy
   */
  async copyToClipboard(url) {
    try {
      await navigator.clipboard.writeText(url);

      // Show success message
      const copyButton = this.refs.copyButton;
      if (copyButton) {
        const labelElement = copyButton.querySelector('.share-menu__label');
        const successText = this.dataset.linkCopiedText || 'Link copied!';

        if (labelElement) {
          // Menu display: update label text
          const originalText = labelElement.textContent;
          labelElement.textContent = successText;

          setTimeout(() => {
            labelElement.textContent = originalText;
          }, 2000);
        } else {
          // Icons display: add visual feedback class and show tooltip
          copyButton.classList.add('share-icon--copied');
          copyButton.setAttribute('data-tooltip', successText);

          setTimeout(() => {
            copyButton.classList.remove('share-icon--copied');
            copyButton.removeAttribute('data-tooltip');
          }, 2000);
        }
      }

      // Track copy event
      this.trackShare('copy-link');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }

  /**
   * Track share event with Shopify Analytics
   * @param {string} platform - Platform name
   */
  trackShare(platform) {
    // Check if Shopify Analytics is available
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.track) {
      try {
        window.ShopifyAnalytics.lib.track('Product Shared', {
          productId: this.dataset.productId,
          productTitle: this.dataset.productTitle,
          platform: platform,
          url: this.dataset.productUrl || window.location.href
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('share', {
      bubbles: true,
      detail: { platform }
    }));
  }
}

customElements.define('share-button', ShareButton);
